import React from "react";
import { NavLink, Outlet } from "react-router-dom";
import { User, Lock, Building2, Gift, Star, Coins, Briefcase, Receipt } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import Breadcrumbs from "@/components/Breadcrumbs";

const TABS = [
  { to: "/app/account", label: "Dati Profilo", icon: User, end: true, testid: "tab-profile" },
  { to: "/app/account/credits", label: "Crediti e piani", icon: Coins, testid: "tab-credits" },
  { to: "/app/account/billing", label: "Fatture crediti", icon: Receipt, testid: "tab-billing" },
  { to: "/app/account/password", label: "Cambio Password", icon: Lock, testid: "tab-password" },
  { to: "/app/account/companies", label: "Aziende", icon: Building2, testid: "tab-companies" },
  { to: "/app/account/referral", label: "Referral", icon: Gift, testid: "tab-referral" },
  { to: "/app/account/reviews", label: "Recensioni", icon: Star, testid: "tab-reviews" },
];

export default function AccountLayout() {
  const { user } = useAuth();
  const isCommercialista = !!user?.is_commercialista;
  const initials = (user?.full_name || user?.email || "?")
    .split(" ").map((s) => s[0]).slice(0, 2).join("").toUpperCase();

  return (
    <div className="p-4 sm:p-6 lg:p-10" data-testid="account-page">
      <Breadcrumbs items={[{ label: "Il tuo Account" }]} />

      {/* Studio-mode context header — only for commercialisti, to make it
          crystal clear these are STUDIO settings (not client's). */}
      {isCommercialista && (
        <div
          className="mb-6 rounded-xl border border-[#E5DBFF] bg-gradient-to-r from-[#F7F3FF] to-white p-4 flex items-start gap-3 flex-wrap"
          data-testid="studio-mode-header"
        >
          <div className="w-11 h-11 rounded-xl bg-[#6D28D9] text-white grid place-items-center shrink-0">
            <Briefcase className="w-5 h-5" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.25em] text-[#6D28D9] font-bold">
              <span className="w-1.5 h-1.5 rounded-full bg-[#6D28D9]" />
              Modalità studio
            </div>
            <div className="mt-1 font-display font-extrabold text-base sm:text-lg text-[#0A0A0A] leading-tight">
              Dati del tuo studio commercialista
            </div>
            <p className="text-xs sm:text-sm text-[#4A4A4A] mt-1 max-w-2xl">
              Qui gestisci il <span className="font-semibold text-[#0A0A0A]">tuo profilo</span>, i crediti, la password, il referral e l&apos;elenco clienti.
              Per modificare i dati fiscali di un singolo cliente, torna al cliente e vai su <span className="font-mono text-[11px] bg-white px-1.5 py-0.5 rounded border border-[#E5DBFF]">Aziende</span>.
            </p>
          </div>
        </div>
      )}

      <div className="mb-8 flex items-center gap-4">
        <div className={`w-14 h-14 rounded-full grid place-items-center text-white font-bold text-xl shrink-0 shadow-md ${
          isCommercialista
            ? "bg-gradient-to-br from-[#6D28D9] to-[#4C1D95] shadow-purple-600/20"
            : "bg-gradient-to-br from-blue-500 to-blue-700 shadow-blue-600/20"
        }`}>
          {initials}
        </div>
        <div>
          <h1 className="font-display font-bold text-3xl lg:text-4xl tracking-tight">
            {isCommercialista ? "Il tuo Studio" : "Il tuo Account"}
          </h1>
          <p className="text-zinc-600 mt-0.5 text-sm">{user?.email}</p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[240px_1fr] gap-6">
        <aside className="rounded-xl border border-[#E8E4D9] bg-white p-2 h-fit sticky top-4">
          <nav className="flex lg:flex-col gap-1 overflow-x-auto lg:overflow-visible" aria-label="Account sections">
            {TABS.map(({ to, label, icon: Icon, end, testid }) => (
              <NavLink
                key={to}
                to={to}
                end={end}
                data-testid={testid}
                className={({ isActive }) => {
                  if (isActive) {
                    return isCommercialista
                      ? "flex items-center gap-2.5 px-3 py-2.5 rounded-lg text-sm font-medium whitespace-nowrap bg-[#F7F3FF] text-[#6D28D9] ring-1 ring-[#E5DBFF]"
                      : "flex items-center gap-2.5 px-3 py-2.5 rounded-lg text-sm font-medium whitespace-nowrap bg-blue-50 text-blue-700 ring-1 ring-blue-200";
                  }
                  return "flex items-center gap-2.5 px-3 py-2.5 rounded-lg text-sm font-medium whitespace-nowrap transition-colors text-zinc-700 hover:bg-[#F5F2EA]";
                }}
              >
                <Icon className="w-4 h-4" />
                {label}
              </NavLink>
            ))}
          </nav>
        </aside>

        <div className="min-w-0">
          <Outlet />
        </div>
      </div>
    </div>
  );
}
